import { useState, useEffect, useCallback } from 'react'
import { mediaApi, type MediaFile } from '../../api/media'
import { useUiStore } from '../../stores/uiStore'

interface Props {
  onBack: () => void
}

const ICONS: Record<string, string> = {
  image: '🖼️',
  video: '🎬',
  audio: '🎵',
  application: '📄',
  text: '📝',
}

function fileIcon(mime: string) {
  return ICONS[mime.split('/')[0]] ?? '📎'
}

function formatBytes(b: number) {
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
  return `${(b / 1024 / 1024).toFixed(1)} MB`
}

export function MediaLibraryPanel({ onBack }: Props) {
  const [files, setFiles] = useState<MediaFile[]>([])
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'image' | 'video' | 'audio' | 'other'>('all')
  const { addToast } = useUiStore()

  useEffect(() => {
    mediaApi.listMyFiles()
      .then((r) => setFiles(r.files ?? []))
      .catch(() => addToast('error', 'Failed to load files'))
      .finally(() => setLoading(false))
  }, [addToast])

  const handleDelete = useCallback(async (id: string) => {
    setDeleting(id)
    try {
      await mediaApi.delete(id)
      setFiles((f) => f.filter((x) => x.id !== id))
      addToast('success', 'File deleted')
    } catch {
      addToast('error', 'Failed to delete file')
    } finally {
      setDeleting(null)
    }
  }, [addToast])

  const filtered = files.filter((f) => {
    if (filter === 'all') return true
    if (filter === 'other') return !['image', 'video', 'audio'].some((t) => f.mime_type.startsWith(t))
    return f.mime_type.startsWith(filter)
  })

  const totalSize = files.reduce((a, f) => a + f.size, 0)

  return (
    <div className="flex flex-col h-full bg-bg-primary">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-divider shrink-0">
        <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary transition-colors">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <h2 className="text-txt-primary font-semibold text-base flex-1">My Files</h2>
        <span className="text-txt-meta text-xs">{formatBytes(totalSize)}</span>
      </div>

      {/* Type filter */}
      <div className="flex gap-1 px-4 py-2 border-b border-divider shrink-0 overflow-x-auto">
        {(['all', 'image', 'video', 'audio', 'other'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setFilter(t)}
            className={`px-3 py-1 rounded-full text-xs font-medium shrink-0 transition-colors
              ${filter === t ? 'bg-accent text-white' : 'bg-bg-input text-txt-secondary hover:bg-bg-hover'}`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="flex justify-center py-8">
            <div className="w-5 h-5 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="text-center py-10">
            <p className="text-3xl mb-2">📭</p>
            <p className="text-txt-secondary text-sm">No files found</p>
          </div>
        )}

        <div className="divide-y divide-divider">
          {filtered.map((f) => (
            <div key={f.id} className="flex items-center gap-3 px-4 py-3 hover:bg-bg-hover transition-colors group">
              {f.mime_type.startsWith('image') ? (
                <img
                  src={mediaApi.getThumbnailURL(f.id, '200x200')}
                  alt={f.file_name}
                  className="w-10 h-10 rounded-lg object-cover shrink-0"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-bg-input flex items-center justify-center text-xl shrink-0">
                  {fileIcon(f.mime_type)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-txt-primary text-sm truncate">{f.file_name}</p>
                <p className="text-txt-meta text-xs">{formatBytes(f.size)} · {new Date(f.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <a
                  href={mediaApi.getURL(f.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-accent text-xs hover:underline shrink-0"
                >
                  Open
                </a>
                <button
                  onClick={() => handleDelete(f.id)}
                  disabled={deleting === f.id}
                  className="text-red-400 hover:text-red-300 text-xs shrink-0 disabled:opacity-40"
                >
                  {deleting === f.id ? '…' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
